import type { QueryClient } from 'react-query';
import {
  Client,
  DiagnosisEnergyUseReadDTO,
  DiagnosisInterventionsReadDTO,
  IDiagnosisInterventionsCreateDTO,
} from '../generated/api-client';
import type { AiSearchEntityType, AiSearchResultItem } from '../services/aiSearchService';
import { submitAiFeedback } from '../services/aiSearchService';
import {
  buildEnergyUseIdMap,
  buildInterventionCloneCreateDto,
  buildVectorIdMap,
} from './interventionCrossDiagnosisClone';

export type ImportAiItemResult = {
  entityType: AiSearchEntityType;
  createdId?: string;
};

async function loadVectorsAndUses(
  queryClient: QueryClient,
  apiClient: Client,
  diagnosisId: string
): Promise<{
  vectors: Awaited<
    ReturnType<
      Client['search_by_diagnosis_id_diagnosis_energy_vectors_search_by_diagnosis_id__diagnosis_id__get']
    >
  >;
  uses: Awaited<
    ReturnType<
      Client['search_by_diagnosis_id_diagnosis_energy_use_search_by_diagnosis_id__diagnosis_id__get']
    >
  >;
}> {
  const [vectors, uses] = await Promise.all([
    queryClient.fetchQuery(
      [`diagnosis-energy-vectors-${diagnosisId}`],
      () =>
        apiClient.search_by_diagnosis_id_diagnosis_energy_vectors_search_by_diagnosis_id__diagnosis_id__get(
          diagnosisId
        )
    ),
    queryClient.fetchQuery(
      [`diagnosis-energy-use-${diagnosisId}`],
      () =>
        apiClient.search_by_diagnosis_id_diagnosis_energy_use_search_by_diagnosis_id__diagnosis_id__get(
          diagnosisId
        )
    ),
  ]);
  return { vectors, uses };
}

function strField(fields: Record<string, unknown>, key: string): string | undefined {
  const v = fields[key];
  if (v == null) return undefined;
  return String(v);
}

function numField(fields: Record<string, unknown>, key: string): number | undefined {
  const v = fields[key];
  if (v == null || v === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

/** Importa un risultato AI nella diagnosi corrente. */
export async function importAiItemToDiagnosis(params: {
  diagnosisId: string;
  item: AiSearchResultItem;
  apiClient: Client;
  queryClient: QueryClient;
  /** Codici già presenti (usi/interventi/indicatori) per evitare duplicati. */
  existingCodes?: string[];
}): Promise<ImportAiItemResult> {
  const { diagnosisId, item, apiClient, queryClient, existingCodes = [] } = params;
  const codeNorm = (strField(item.payload, 'code') || '').trim();
  if (codeNorm && existingCodes.some(c => c.trim() === codeNorm)) {
    throw new Error(`Esiste già un elemento con codice "${codeNorm}" nella diagnosi attuale.`);
  }

  if (item.entityType === 'energy_use') {
    if (item.tipo === 'external') {
      await apiClient.create_item_diagnosis_energy_use__post({
        diagnosis_id: diagnosisId,
        code: strField(item.payload, 'code') || 'AI-EXT',
        description: strField(item.payload, 'description'),
      } as any);
      await submitAiFeedback(item.uid, 1);
      return { entityType: 'energy_use' };
    }

    let row: DiagnosisEnergyUseReadDTO | null = null;
    try {
      row = await apiClient.get_item_diagnosis_energy_use__item_id__get(item.id_source);
    } catch {
      row = null;
    }
    if (row?.original_energy_use_id) {
      await apiClient.copy_energy_use_diagnosis_energy_use_copy_post({
        energy_use_id: row.original_energy_use_id,
        diagnosis_id: diagnosisId,
      } as any);
    } else if (row) {
      await apiClient.create_item_diagnosis_energy_use__post({
        diagnosis_id: diagnosisId,
        code: row.code || '',
        description: row.description,
        original_energy_use_id: row.original_energy_use_id,
      } as any);
    } else {
      await apiClient.copy_energy_use_diagnosis_energy_use_copy_post({
        energy_use_id: item.id_source,
        diagnosis_id: diagnosisId,
      } as any);
    }
    return { entityType: 'energy_use' };
  }

  if (item.entityType === 'intervention') {
    if (item.tipo === 'external') {
      const savingExt =
        numField(item.payload, 'expected_economical_saving') ??
        numField(item.payload, 'expected_economic_saving');
      const dto: IDiagnosisInterventionsCreateDTO = {
        diagnosis_id: diagnosisId,
        code: strField(item.payload, 'code') || strField(item.payload, 'title') || 'AI-EXT',
        title: strField(item.payload, 'title'),
        description: strField(item.payload, 'description'),
        type: strField(item.payload, 'type'),
        comments: strField(item.payload, 'comments'),
        investment: numField(item.payload, 'investment'),
        expected_economic_saving: savingExt,
        payback_time: numField(item.payload, 'payback_time'),
        energy_vectors: [],
        energy_uses: [],
        all_energy_use: false,
      };
      await apiClient.create_item_diagnosis_interventions__post(dto as any);
      await submitAiFeedback(item.uid, 1);
      return { entityType: 'intervention' };
    }

    const source = await apiClient.get_item_diagnosis_interventions__item_id__get(item.id_source);
    const sourceDx = source.diagnosis_id;
    if (!sourceDx) {
      throw new Error('Intervento interno senza diagnosi sorgente.');
    }
    const { vectors: targetVec, uses: targetUses } = await loadVectorsAndUses(
      queryClient,
      apiClient,
      diagnosisId
    );
    let srcVec = await queryClient.fetchQuery(
      [`diagnosis-energy-vectors-${sourceDx}`],
      () =>
        apiClient.search_by_diagnosis_id_diagnosis_energy_vectors_search_by_diagnosis_id__diagnosis_id__get(
          sourceDx
        )
    );
    let srcUses = await queryClient.fetchQuery(
      [`diagnosis-energy-use-${sourceDx}`],
      () =>
        apiClient.search_by_diagnosis_id_diagnosis_energy_use_search_by_diagnosis_id__diagnosis_id__get(
          sourceDx
        )
    );
    const vMap = buildVectorIdMap(srcVec, targetVec);
    const uMap = buildEnergyUseIdMap(srcUses, targetUses);
    const payload = buildInterventionCloneCreateDto(source, diagnosisId, vMap, uMap);
    if (!payload) {
      throw new Error(
        'Impossibile mappare vettori o usi energetici verso la diagnosi attuale. Verifica che le configurazioni coincidano.'
      );
    }
    await apiClient.create_item_diagnosis_interventions__post(payload as any);
    return { entityType: 'intervention' };
  }

  if (item.entityType === 'indicator') {
    if (item.tipo === 'external') {
      await apiClient.create_with_relations_diagnosis_indicators_create_with_relations_post({
        diagnosis_id: diagnosisId,
        code: strField(item.payload, 'code'),
        notes: strField(item.payload, 'description'),
        formula: strField(item.payload, 'formula'),
        udm_desiderata: strField(item.payload, 'unit'),
        meters: [],
        variables: [],
        consumptions: [],
      } as any);
      await submitAiFeedback(item.uid, 1);
      return { entityType: 'indicator' };
    }

    const source = await apiClient.get_item_diagnosis_indicators__item_id__get(item.id_source);
    await apiClient.create_with_relations_diagnosis_indicators_create_with_relations_post({
      diagnosis_id: diagnosisId,
      code: source.code,
      index: source.index,
      formula: source.formula,
      udm_desiderata: source.udm_desiderata,
      energy_use_id: source.energy_use_id,
      correlation_r2: source.correlation_r2,
      reference_ipe: source.reference_ipe,
      reference_ipe_unit: source.reference_ipe_unit,
      min_relevant_variable_field: source.min_relevant_variable_field,
      max_relevant_variable_field: source.max_relevant_variable_field,
      reliability_level: source.reliability_level,
      ipe_source: source.ipe_source,
      report_table_graph: source.report_table_graph,
      notes: source.notes,
      meters: [],
      variables: [],
      consumptions: [],
    } as any);
    return { entityType: 'indicator' };
  }

  throw new Error(`Tipo entità non supportato: ${item.entityType}`);
}

/** Converte un risultato AI in DTO intervento per griglia (interno da API o esterno sintetico). */
export async function resolveAiInterventionRow(
  item: AiSearchResultItem,
  apiClient: Client
): Promise<DiagnosisInterventionsReadDTO> {
  if (item.tipo === 'internal') {
    return apiClient.get_item_diagnosis_interventions__item_id__get(item.id_source);
  }
  const f = item.payload;
  return DiagnosisInterventionsReadDTO.fromJS({
    id: `ai-ext-${item.uid}`,
    code: strField(f, 'code') || strField(f, 'title') || item.uid,
    title: strField(f, 'title'),
    description: strField(f, 'description'),
    type: strField(f, 'type'),
    comments: strField(f, 'comments'),
    investment: numField(f, 'investment'),
    expected_economic_saving:
      numField(f, 'expected_economical_saving') ?? numField(f, 'expected_economic_saving'),
    payback_time: numField(f, 'payback_time'),
    energy_vectors: [],
    energy_uses: [],
    all_energy_use: false,
  });
}

/** Converte un risultato AI in DTO uso energetico per griglia. */
export async function resolveAiEnergyUseRow(
  item: AiSearchResultItem,
  apiClient: Client
): Promise<DiagnosisEnergyUseReadDTO> {
  if (item.tipo === 'internal') {
    try {
      return await apiClient.get_item_diagnosis_energy_use__item_id__get(item.id_source);
    } catch {
      return DiagnosisEnergyUseReadDTO.fromJS({
        id: item.id_source,
        code: strField(item.payload, 'code') || item.id_source,
        description: strField(item.payload, 'description'),
      });
    }
  }
  return DiagnosisEnergyUseReadDTO.fromJS({
    id: `ai-ext-${item.uid}`,
    code: strField(item.payload, 'code') || 'AI-EXT',
    description: strField(item.payload, 'description'),
  });
}
